import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import type { CompleteInterventionInput, InterventionDetail } from "@techplanner/shared";
import { useAuth } from "../auth";
import { api, ApiError, errorMessage, newIdempotencyKey } from "./api";
import { clearDraft } from "./hooks";
import { keys, useInvalidate, useQueryClient } from "./queries";

/** Clôture d'intervention terminée sur le téléphone, en attente d'envoi au serveur. */
export interface PendingClosure {
  /** Clé d'idempotence, envoyée en en-tête Idempotency-Key à chaque tentative. */
  key: string;
  interventionId: number;
  payload: CompleteInterventionInput;
  createdAt: string;
  state: "pending" | "sending" | "refused";
  error?: string;
}

export type SendResult = "sent" | "pending" | "refused";
type Sender = (item: PendingClosure) => Promise<unknown>;

const FULL_MESSAGE = "Impossible d'enregistrer sur ce téléphone : espace de stockage plein.";
const FLUSH_INTERVAL_MS = 30_000;
/** Délai maximal d'un envoi de clôture ; au-delà, elle reste en attente et repartira. */
const CLOSURE_TIMEOUT_MS = 30_000;

/** Signal qui expire après `ms` ; repli pour les navigateurs sans AbortSignal.timeout (iOS 15 et avant). */
function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(new DOMException("Délai dépassé.", "TimeoutError")), ms);
  return controller.signal;
}

/** Une file par compte : un autre compte du même téléphone ne la voit pas et ne l'envoie pas. */
export function closureStorageKey(companyCode: string, userId: number): string {
  return `closures:${companyCode}:${userId}`;
}

const STATES = ["pending", "sending", "refused"];

function isClosure(value: unknown): value is PendingClosure {
  const v = value as Partial<PendingClosure> | null;
  return (
    !!v &&
    typeof v === "object" &&
    typeof v.key === "string" &&
    typeof v.interventionId === "number" &&
    !!v.payload &&
    typeof v.payload === "object" &&
    typeof v.state === "string" &&
    STATES.includes(v.state)
  );
}

export class ClosureStore {
  private items: PendingClosure[];
  private readonly listeners = new Set<() => void>();
  private chain: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly storageKey: string,
    private readonly storage: Storage = localStorage
  ) {
    // Un envoi interrompu par la fermeture de l'appli doit être repris.
    const stored = this.read();
    this.items = stored.map((item) => (item.state === "sending" ? { ...item, state: "pending" as const } : item));
    // Le retour à « en attente » est aussi écrit : sinon la prochaine relecture le perdrait.
    if (stored.some((item) => item.state === "sending")) {
      try {
        this.storage.setItem(this.storageKey, JSON.stringify(this.items));
      } catch {
        /* stockage plein : l'état reste en mémoire */
      }
    }
  }

  private backUp(raw: string): void {
    try {
      const backup = `${this.storageKey}:corrompu`;
      if (this.storage.getItem(backup) === null) this.storage.setItem(backup, raw);
    } catch {
      /* rien */
    }
  }

  /** Lit la file sur le téléphone. Un contenu illisible est mis de côté, jamais écrasé en silence. */
  private read(): PendingClosure[] {
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(this.storageKey);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) throw new Error("format inattendu");
      return parsed.filter(isClosure);
    } catch {
      if (raw) this.backUp(raw);
      return [];
    }
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  /** Relit le stockage (autre onglet ou fenêtre du même compte) puis applique le changement. */
  private commit(change: (items: PendingClosure[]) => PendingClosure[], strict: boolean): void {
    const next = change(this.read());
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(next));
    } catch {
      // Un simple changement d'état se garde en mémoire ; seul l'ajout doit réussir sur le téléphone.
      if (strict) throw new Error(FULL_MESSAGE);
    }
    this.items = next;
    this.notify();
  }

  /** Relit le stockage après un changement fait par un autre onglet. */
  refresh(): void {
    this.items = this.read();
    this.notify();
  }

  /** Exécute les envois l'un après l'autre : un seul à la fois pour cette file. */
  serial<T>(task: () => Promise<T>): Promise<T> {
    const run = this.chain.then(task, task);
    this.chain = run.catch(() => undefined);
    return run;
  }

  list(): PendingClosure[] {
    return this.items;
  }

  forIntervention(id: number): PendingClosure | undefined {
    return this.items.find((item) => item.interventionId === id);
  }

  add(interventionId: number, payload: CompleteInterventionInput): PendingClosure {
    const item: PendingClosure = { key: newIdempotencyKey(), interventionId, payload, createdAt: new Date().toISOString(), state: "pending" };
    this.commit((items) => [...items, item], true);
    return item;
  }

  update(key: string, patch: Partial<PendingClosure>): void {
    this.commit((items) => items.map((item) => (item.key === key ? { ...item, ...patch } : item)), false);
  }

  remove(key: string): void {
    this.commit((items) => items.filter((item) => item.key !== key), false);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

/** Erreurs passagères : réseau coupé, session expirée, délai dépassé, trop de requêtes, panne serveur. */
function isTemporary(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status === 0 || err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500;
}

type Attempt = SendResult | "skipped";

function attempt(store: ClosureStore, key: string, send: Sender, isCurrent: () => boolean): Promise<Attempt> {
  return store.serial(async (): Promise<Attempt> => {
    const current = store.list().find((entry) => entry.key === key);
    if (!current) return "sent";
    // Refusée, ou en cours d'envoi dans un autre onglet : on passe son tour.
    if (current.state !== "pending") return "skipped";
    // Le compte a changé entre-temps : rien ne part avec la session d'un autre.
    if (!isCurrent()) return "pending";
    store.update(key, { state: "sending", error: undefined });
    try {
      await send(current);
    } catch (err) {
      if (isTemporary(err)) {
        store.update(key, { state: "pending" });
        return "pending";
      }
      store.update(key, { state: "refused", error: errorMessage(err) });
      return "refused";
    }
    store.remove(key);
    return "sent";
  });
}

/**
 * Envoie une clôture (l'une après l'autre avec les autres envois de la file). Réseau coupé, erreur
 * passagère ou session expirée : elle reste en attente. Tout autre refus du serveur est définitif.
 */
export async function sendClosure(store: ClosureStore, item: PendingClosure, send: Sender, isCurrent: () => boolean = () => true): Promise<SendResult> {
  const result = await attempt(store, item.key, send, isCurrent);
  if (result !== "skipped") return result;
  return store.list().find((entry) => entry.key === item.key)?.state === "refused" ? "refused" : "pending";
}

/**
 * Envoie les clôtures en attente, une par une, dans l'ordre. S'arrête si le réseau ou la session manque,
 * ou dès que `isCurrent` dit que le compte n'est plus le même.
 */
export async function flushClosures(store: ClosureStore, send: Sender, isCurrent: () => boolean = () => true): Promise<number> {
  let sent = 0;
  for (const item of store.list().filter((entry) => entry.state === "pending")) {
    if (!isCurrent()) break;
    const result = await attempt(store, item.key, send, isCurrent);
    if (result === "sent") sent++;
    else if (result === "pending") break;
  }
  return sent;
}

/** Confirmation de déconnexion quand des bons restent à envoyer depuis ce téléphone. */
export function unsentLogoutMessage(count: number): string {
  return count > 1
    ? `${count} bons ne sont pas encore envoyés. Ils partiront à votre prochaine connexion sur ce téléphone. Se déconnecter ?`
    : "1 bon n'est pas encore envoyé. Il partira à votre prochaine connexion sur ce téléphone. Se déconnecter ?";
}

interface ClosuresApi {
  items: PendingClosure[];
  store: ClosureStore | null;
  submit(interventionId: number, payload: CompleteInterventionInput): Promise<SendResult>;
  retry(key: string): Promise<SendResult>;
  abandon(key: string): void;
}

const ClosuresContext = createContext<ClosuresApi | null>(null);
const NO_ITEMS: PendingClosure[] = [];
const NO_SUBSCRIPTION = () => () => undefined;

export function ClosuresProvider({ children }: { children: ReactNode }) {
  const { company, user } = useAuth();
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  const companyCode = company?.code;
  const userId = user?.id;
  const store = useMemo(
    () => (companyCode && userId !== undefined ? new ClosureStore(closureStorageKey(companyCode, userId)) : null),
    [companyCode, userId]
  );

  const items = useSyncExternalStore(
    store ? (listener) => store.subscribe(listener) : NO_SUBSCRIPTION,
    () => (store ? store.list() : NO_ITEMS)
  );

  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  const send = useCallback<Sender>(
    async (item) => {
      const detail = await api<InterventionDetail>(`/interventions/${item.interventionId}/complete`, {
        method: "POST",
        body: item.payload,
        idempotencyKey: item.key,
        // Réseau qui se dit présent mais ne répond pas : sans délai, toute la file attendrait.
        // L'expiration devient une erreur réseau, donc la clôture reste en attente.
        signal: timeoutSignal(CLOSURE_TIMEOUT_MS)
      });
      // Une lecture de la fiche partie avant la clôture (retour du réseau) ne doit pas la ramener « à faire ».
      await qc.cancelQueries({ queryKey: keys.intervention(item.interventionId) });
      qc.setQueryData(keys.intervention(item.interventionId), detail);
      clearDraft(`report:${item.interventionId}`);
      void invalidateRef.current();
      return detail;
    },
    [qc]
  );

  // Une seule vidange à la fois, même si plusieurs déclencheurs arrivent ensemble.
  const currentStore = useRef<ClosureStore | null>(null);
  currentStore.current = store;
  const isCurrent = useCallback(() => currentStore.current === store, [store]);
  const flushing = useRef<{ store: ClosureStore; promise: Promise<number> } | null>(null);
  const flush = useCallback(() => {
    if (!store || !navigator.onLine) return Promise.resolve(0);
    if (flushing.current?.store !== store) {
      const promise = flushClosures(store, send, isCurrent).finally(() => {
        if (flushing.current?.promise === promise) flushing.current = null;
      });
      flushing.current = { store, promise };
    }
    return flushing.current.promise;
  }, [store, send, isCurrent]);

  const pendingCount = items.filter((item) => item.state === "pending").length;

  // Au montage, à l'ouverture de la file d'un compte (connexion) et au retour du réseau.
  useEffect(() => {
    void flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [flush]);

  // Autre onglet ou fenêtre du même compte : on relit la file.
  useEffect(() => {
    if (!store) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === closureStorageKey(companyCode ?? "", userId ?? 0)) store.refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [store, companyCode, userId]);

  useEffect(() => {
    if (pendingCount === 0) return;
    const timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [flush, pendingCount]);

  const value = useMemo<ClosuresApi>(
    () => ({
      items,
      store,
      async submit(interventionId, payload) {
        if (!store) throw new Error("Session absente");
        const item = store.add(interventionId, payload);
        if (!navigator.onLine) return "pending";
        return sendClosure(store, item, send, isCurrent);
      },
      async retry(key) {
        const item = store?.list().find((entry) => entry.key === key);
        if (!store || !item) return "sent";
        store.update(key, { state: "pending", error: undefined });
        return navigator.onLine ? sendClosure(store, item, send, isCurrent) : "pending";
      },
      abandon(key) {
        store?.remove(key);
      }
    }),
    [items, store, send, isCurrent]
  );

  return createElement(ClosuresContext.Provider, { value }, children);
}

export function useClosures(): ClosuresApi {
  const ctx = useContext(ClosuresContext);
  if (!ctx) throw new Error("useClosures hors de ClosuresProvider");
  return ctx;
}
